import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  ChecklistCountsResponseDTO,
  ChecklistProgressResponseDTO,
  ChecklistItemsResponseDTO,
  AttachmentResponseDTO,
  SuccessResponseDTO,
  MarkItemDoneRequestDTO,
  MarkItemInProgressRequestDTO,
  MarkItemToBeDoneLaterRequestDTO,
  CloseMonthRequestDTO,
} from "../models/ChecklistDTOs";
import { API_BASE_URL } from "../../../../config/api";
import type { ChecklistItemsTabStatus } from "../../domain/entities/ChecklistEntity";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * Get checklist counts by accounting area
 * GET /api/month-end/checklists/counts?month=YYYY-MM
 */
export const getChecklistCounts = async (
  month?: string,
): Promise<ChecklistCountsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  if (month) {
    queryParams.append("month", month);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/counts?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get checklist progress statistics
 * GET /api/month-end/checklists/progress?month=YYYY-MM
 */
export const getChecklistProgress = async (
  month?: string,
): Promise<ChecklistProgressResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  if (month) {
    queryParams.append("month", month);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/progress?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get checklist items by accounting area with pagination
 * GET /api/month-end/checklists/items?month=YYYY-MM&accounting_area={area}&offset=0&limit=20&status={status}
 */
export const getChecklistItemsByArea = async (
  month: string,
  accountingArea: string,
  offset: number = 0,
  limit: number = 20,
  status?: ChecklistItemsTabStatus | null,
): Promise<ChecklistItemsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  queryParams.append("month", month);
  queryParams.append("accounting_area", accountingArea);
  queryParams.append("offset", offset.toString());
  queryParams.append("limit", limit.toString());
  if (status) {
    queryParams.append("status", status);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get checklist items across all accounting areas with pagination.
 * GET /api/month-end/checklists/items/all?month=YYYY-MM&offset=0&limit=20&status={status}
 */
export const getChecklistItemsAllAreas = async (
  month: string,
  offset: number = 0,
  limit: number = 20,
  status?: ChecklistItemsTabStatus | null,
): Promise<ChecklistItemsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  queryParams.append("month", month);
  queryParams.append("offset", offset.toString());
  queryParams.append("limit", limit.toString());
  if (status) {
    queryParams.append("status", status);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/all?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Search checklist items by title across all accounting areas (paginated).
 * GET /api/month-end/checklists/items/search-all?month=YYYY-MM&q={term}&offset=0&limit=20&status={status}
 */
export const searchChecklistItemsByTitleAllAreas = async (
  month: string,
  q: string,
  offset: number = 0,
  limit: number = 20,
  status?: ChecklistItemsTabStatus | null,
): Promise<ChecklistItemsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  queryParams.append("month", month);
  queryParams.append("q", q);
  queryParams.append("offset", offset.toString());
  queryParams.append("limit", limit.toString());
  if (status) {
    queryParams.append("status", status);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/search-all?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Search checklist items by title within an accounting area (paginated).
 * GET /api/month-end/checklists/items/search?month=YYYY-MM&accounting_area={area}&q={term}&offset=0&limit=20&status={status}
 */
export const searchChecklistItemsByTitleInArea = async (
  month: string,
  accountingArea: string,
  q: string,
  offset: number = 0,
  limit: number = 20,
  status?: ChecklistItemsTabStatus | null,
): Promise<ChecklistItemsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();
  queryParams.append("month", month);
  queryParams.append("accounting_area", accountingArea);
  queryParams.append("q", q);
  queryParams.append("offset", offset.toString());
  queryParams.append("limit", limit.toString());
  if (status) {
    queryParams.append("status", status);
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/search?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Mark checklist item as done
 * POST /api/month-end/checklists/items/mark-done
 */
export const markItemDone = async (
  itemId: number,
  notes?: string,
  description?: string,
): Promise<SuccessResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/mark-done`;

  const body: MarkItemDoneRequestDTO = {
    item_id: itemId,
  };
  if (notes) {
    body.notes = notes;
  }
  if (description) {
    body.description = description;
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Mark checklist item as in progress
 * POST /api/month-end/checklists/items/mark-in-progress
 */
export const markItemInProgress = async (
  itemId: number,
): Promise<SuccessResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/mark-in-progress`;

  const body: MarkItemInProgressRequestDTO = {
    item_id: itemId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Mark checklist item as to be done later (counts toward month close).
 * POST /api/month-end/checklists/items/mark-to-be-done-later
 */
export const markItemToBeDoneLater = async (
  itemId: number,
): Promise<SuccessResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/mark-to-be-done-later`;

  const body: MarkItemToBeDoneLaterRequestDTO = {
    item_id: itemId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Upload attachment (screenshot) for checklist item
 * POST /api/month-end/checklists/items/attachments/upload
 */
export const uploadAttachment = async (
  itemId: number,
  file: File,
): Promise<AttachmentResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/attachments/upload`;

  const formData = new FormData();
  formData.append("item_id", itemId.toString());
  formData.append("file", file);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type header - browser will set it with boundary for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Delete attachment
 * POST /api/month-end/checklists/items/attachments/delete
 */
export const deleteAttachment = async (
  attachmentId: number,
): Promise<SuccessResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/items/attachments/delete`;

  const body = {
    attachment_id: attachmentId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Close month (mark checklist as closed)
 * POST /api/month-end/checklists/close
 */
export const closeMonth = async (
  checklistId: number,
): Promise<SuccessResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/checklists/close`;

  const body: CloseMonthRequestDTO = {
    checklist_id: checklistId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Trigger CFO cron_client_month (generate monthly report and persist mapped values).
 * Called after month close to sync client monthly data.
 * GET /api/cfo/cron_client_month?year=...&month=...&company_id=...
 */
export const triggerCronClientMonth = async (
  year: number,
  month: number,
): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const params = new URLSearchParams({
    year: String(year),
    month: String(month),
  });
  const url = `${API_BASE_URL}/api/cfo/cron_client_month?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }
};
